import { useEffect } from 'react'
import type { RefObject } from 'react'

export interface DismissOptions {
  /**
   * A second element that counts as inside even though it is not a descendant of
   * `ref` — a phone bottom sheet is portalled to the body to escape any transformed
   * ancestor, so without it every tap on the sheet would dismiss it.
   */
  alsoInside?: RefObject<HTMLElement | null>
  /**
   * Selector for targets that neither dismiss nor count as inside: the surface
   * replaces its own contents when they are clicked. The word panel names the
   * passage's word tokens here, so moving from one word to the next swaps the entry
   * instead of closing the panel and opening a new one — which, on a wide screen,
   * slid the passage back and forth and made the second click miss.
   */
  ignore?: string
}

/**
 * Dismisses a popover/dropdown when clicking outside the ref element or pressing
 * Escape. Only attaches listeners when `active` is true.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  active: boolean,
  options: DismissOptions = {},
) {
  const { alsoInside, ignore } = options

  useEffect(() => {
    if (!active) return

    function handlePointer(e: MouseEvent | TouchEvent) {
      const target = e.target as Node
      if (!ref.current || ref.current.contains(target)) return
      if (alsoInside?.current?.contains(target)) return
      if (ignore && target instanceof Element && target.closest(ignore)) return
      onDismiss()
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss()
    }

    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('touchstart', handlePointer, { passive: true })
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('touchstart', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [ref, onDismiss, active, alsoInside, ignore])
}
